import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { useContactDrawer } from "../context/ContactDrawerContext";

export default function About() {
  const { openDrawer } = useContactDrawer();

  return (
    <div className="pt-[72px] bg-white min-h-screen">
      <SEO
        title="Om os | Elmegaarden Have og Anlæg"
        description="Tekst er på vej."
        ogImage="https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/elmegaarden-0006.jpg"
        canonical="/om-os"
      />

      {/* Story & Philosophy */}
      <section className="pt-24 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Image */}
          <div className="lg:col-span-6 aspect-16/11 overflow-hidden rounded-sm border border-brand-sand-200 shadow-sm relative">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/elmegaarden-0006.jpg"
              alt="Om os - Elmegaarden Have og Anlæg"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="lg:col-span-6 flex flex-col gap-6 text-left">
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-brand-green-900 leading-tight">
              Om os
            </h1>
            <div className="flex flex-col gap-4 text-brand-charcoal/80 leading-relaxed text-sm md:text-base">
              <p className="font-medium text-brand-green-950 text-base md:text-lg">
                Elmegaarden Have og Anlæg er din pålidelige partner i Hobro, Arden, Hadsund og resten af Nordjylland.
              </p>
              <p>
                Vi brænder for at levere anlægsgartnerarbejde i højeste kvalitet – fra planlægning og rådgivning til færdig belægning, støttemure, nye indkørsler, terrasser og grøn havepleje.
              </p>
              <p>
                Som autoriseret kloakmestervirksomhed under <strong>Byggeriets Kvalitetskontrol</strong> sikrer vi desuden, at alle kloak- og afvandingsopgaver udføres efter gældende standarder med fuld tryghed for dig.
              </p>
            </div>
            <div className="mt-2 flex flex-wrap gap-4">
              <button
                onClick={openDrawer}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs sm:text-sm tracking-wide uppercase rounded-sm transition-all duration-300 shadow-sm cursor-pointer"
              >
                Få et tilbud
              </button>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-brand-sand-100 hover:bg-brand-sand-200 text-brand-green-900 font-bold text-xs sm:text-sm tracking-wide uppercase rounded-sm transition-all duration-300 border border-brand-sand-300"
              >
                Kontakt os her <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-brand-sand-100 border-t border-brand-sand-300/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto flex flex-col gap-4 mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-green-900">
              Hvad vi står for
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Value 1 */}
            <div className="group bg-white p-8 md:p-10 rounded-sm border border-brand-sand-200/60 shadow-xs hover:shadow-md hover:border-brand-green-600/30 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between relative overflow-hidden text-left">
              <div>
                <div className="flex justify-end">
                  <span className="text-brand-sand-300/50 text-4xl font-extrabold font-display select-none">
                    01
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight text-brand-green-900 mt-8 mb-3">
                  Faglig Stolthed
                </h3>
                <p className="text-sm text-brand-charcoal/80 leading-relaxed">
                  Vi udfører alt arbejde efter de højeste faglige standarder. Vi springer aldrig over, hvor gærdet er lavest, og sikrer holdbare løsninger.
                </p>
              </div>
            </div>

            {/* Value 2 */}
            <div className="group bg-white p-8 md:p-10 rounded-sm border border-brand-sand-200/60 shadow-xs hover:shadow-md hover:border-brand-green-600/30 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between relative overflow-hidden text-left">
              <div>
                <div className="flex justify-end">
                  <span className="text-brand-sand-300/50 text-4xl font-extrabold font-display select-none">
                    02
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight text-brand-green-900 mt-8 mb-3">
                  Ærlig Rådgivning
                </h3>
                <p className="text-sm text-brand-charcoal/80 leading-relaxed">
                  Vi rådgiver altid ærligt om materialevalg, holdbarhed og vedligeholdelse, så du får en have, der passer til dine behov og dit budget.
                </p>
              </div>
            </div>

            {/* Value 3 */}
            <div className="group bg-white p-8 md:p-10 rounded-sm border border-brand-sand-200/60 shadow-xs hover:shadow-md hover:border-brand-green-600/30 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between relative overflow-hidden text-left">
              <div>
                <div className="flex justify-end">
                  <span className="text-brand-sand-300/50 text-4xl font-extrabold font-display select-none">
                    03
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight text-brand-green-900 mt-8 mb-3">
                  Grøn Passion
                </h3>
                <p className="text-sm text-brand-charcoal/80 leading-relaxed">
                  Vi elsker naturen og planteriget. Vi designer haver med fokus på biodiversitet, sund jord og planter, der trives i det danske klima.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-brand-sand-200/50 text-brand-charcoal">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-green-900">
            Lad os skabe dit drømmeuderum sammen
          </h2>
          <p className="text-sm md:text-base text-brand-charcoal/80 max-w-2xl leading-relaxed">
            Vores erfarne anlægsgartnere og autoriserede kloakmestre står klar til at rådgive dig og omsætte dine idéer til virkelighed. Kontakt os i dag for et uforpligtende møde.
          </p>
          <button
            onClick={openDrawer}
            className="mt-4 px-5 py-3 sm:px-8 sm:py-4 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs sm:text-sm tracking-wide uppercase rounded-sm transition-all duration-300 hover:shadow-xl cursor-pointer focus:outline-none"
          >
            Kontakt os i dag
          </button>
        </div>
      </section>
    </div>
  );
}
